import type {
    ClinicData,
    FollowUp,
    Patient,
    Prescription,
    ReminderLog,
    Visit
} from '@/lib/clinic/types'

import { dateFromTimestamp, diffDays, isSameMonth, timeFromTimestamp } from '@/lib/clinic/dates'
import { CALL_OUTCOME_LABELS, digitsOnly } from '@/lib/clinic/format'

export interface PatientStats {
    totalVisits: number
    lastVisit: string | null
    firstVisit: string | null
    pendingFollowUp: FollowUp | null
}

export interface ClinicIndex {
    patientsById: Map<string, Patient>
    visitsById: Map<string, Visit>
    visitsByPatient: Map<string, Visit[]>
    prescriptionsById: Map<string, Prescription>
    prescriptionsByPatient: Map<string, Prescription[]>
    prescriptionsByVisit: Map<string, Prescription>
    followUpsById: Map<string, FollowUp>
    followUpsByPatient: Map<string, FollowUp[]>
    followUpsByVisit: Map<string, FollowUp>
    remindersByFollowUp: Map<string, ReminderLog[]>
    remindersByPatient: Map<string, ReminderLog[]>
    stats: Map<string, PatientStats>
}

const EMPTY_STATS: PatientStats = { totalVisits: 0, lastVisit: null, firstVisit: null, pendingFollowUp: null }

const pushTo = <T,>(map: Map<string, T[]>, key: string, value: T): void => {
    const list = map.get(key)

    if (list) list.push(value)
    else map.set(key, [value])
}

/** Visits and prescriptions newest first, comparing the date and then when they were saved. */
const byDateDesc = (a: { date: string, createdAt: string }, b: { date: string, createdAt: string }): number => {
    return b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt)
}

export const buildIndex = (data: ClinicData): ClinicIndex => {
    const patientsById = new Map(data.patients.map(patient => [patient.id, patient]))
    const visitsById = new Map<string, Visit>()
    const visitsByPatient = new Map<string, Visit[]>()
    const prescriptionsById = new Map<string, Prescription>()
    const prescriptionsByPatient = new Map<string, Prescription[]>()
    const prescriptionsByVisit = new Map<string, Prescription>()
    const followUpsById = new Map<string, FollowUp>()
    const followUpsByPatient = new Map<string, FollowUp[]>()
    const followUpsByVisit = new Map<string, FollowUp>()
    const remindersByFollowUp = new Map<string, ReminderLog[]>()
    const remindersByPatient = new Map<string, ReminderLog[]>()

    data.visits.forEach(visit => {
        visitsById.set(visit.id, visit)
        pushTo(visitsByPatient, visit.patientId, visit)
    })

    data.prescriptions.forEach(prescription => {
        prescriptionsById.set(prescription.id, prescription)
        pushTo(prescriptionsByPatient, prescription.patientId, prescription)

        if (prescription.visitId) {
            const existing = prescriptionsByVisit.get(prescription.visitId)

            if (!existing || existing.createdAt < prescription.createdAt) prescriptionsByVisit.set(prescription.visitId, prescription)
        }
    })

    data.followUps.forEach(followUp => {
        followUpsById.set(followUp.id, followUp)
        pushTo(followUpsByPatient, followUp.patientId, followUp)

        if (followUp.visitId) {
            const existing = followUpsByVisit.get(followUp.visitId)
            const preferred = !existing
                || (followUp.status === 'pending' && existing.status !== 'pending')
                || ((followUp.status === 'pending') === (existing.status === 'pending') && followUp.createdAt > existing.createdAt)

            if (preferred) followUpsByVisit.set(followUp.visitId, followUp)
        }
    })

    data.reminders.forEach(reminder => {
        pushTo(remindersByPatient, reminder.patientId, reminder)

        if (reminder.followUpId) pushTo(remindersByFollowUp, reminder.followUpId, reminder)
    })

    visitsByPatient.forEach(list => list.sort(byDateDesc))
    prescriptionsByPatient.forEach(list => list.sort(byDateDesc))
    followUpsByPatient.forEach(list => list.sort((a, b) => b.dueDate.localeCompare(a.dueDate)))
    remindersByFollowUp.forEach(list => list.sort((a, b) => b.createdAt.localeCompare(a.createdAt)))
    remindersByPatient.forEach(list => list.sort((a, b) => b.createdAt.localeCompare(a.createdAt)))

    const stats = new Map<string, PatientStats>()

    data.patients.forEach(patient => {
        const visits = visitsByPatient.get(patient.id) ?? []
        const pending = (followUpsByPatient.get(patient.id) ?? [])
            .filter(followUp => followUp.status === 'pending')
            .sort((a, b) => a.dueDate.localeCompare(b.dueDate))

        stats.set(patient.id, {
            totalVisits: visits.length,
            lastVisit: visits[0]?.date ?? null,
            firstVisit: visits[visits.length - 1]?.date ?? null,
            pendingFollowUp: pending[0] ?? null
        })
    })

    return {
        patientsById,
        visitsById,
        visitsByPatient,
        prescriptionsById,
        prescriptionsByPatient,
        prescriptionsByVisit,
        followUpsById,
        followUpsByPatient,
        followUpsByVisit,
        remindersByFollowUp,
        remindersByPatient,
        stats
    }
}

export const getStats = (index: ClinicIndex, patientId: string): PatientStats => index.stats.get(patientId) ?? EMPTY_STATS

export type FollowUpBucket = 'today' | 'tomorrow' | 'upcoming' | 'overdue' | 'completed' | 'cancelled'

export const FOLLOW_UP_BUCKETS: Array<{ key: FollowUpBucket, label: string }> = [
    { key: 'today', label: 'Due today' },
    { key: 'tomorrow', label: 'Due tomorrow' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' }
]

export const getFollowUpBucket = (followUp: FollowUp, today: string): FollowUpBucket => {
    if (followUp.status === 'completed') return 'completed'
    if (followUp.status === 'cancelled') return 'cancelled'

    const diff = diffDays(followUp.dueDate, today)

    if (diff < 0) return 'overdue'
    if (diff === 0) return 'today'
    if (diff === 1) return 'tomorrow'

    return 'upcoming'
}

export const sortFollowUps = (bucket: FollowUpBucket, list: FollowUp[]): FollowUp[] => {
    const timeKey = (followUp: FollowUp): string => `${followUp.dueDate} ${followUp.dueTime ?? '99:99'}`

    if (bucket === 'completed') return [...list].sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))
    if (bucket === 'cancelled') return [...list].sort((a, b) => (b.cancelledAt ?? '').localeCompare(a.cancelledAt ?? ''))
    if (bucket === 'overdue') return [...list].sort((a, b) => a.dueDate.localeCompare(b.dueDate))

    return [...list].sort((a, b) => timeKey(a).localeCompare(timeKey(b)))
}

export const groupFollowUps = (followUps: FollowUp[], today: string): Record<FollowUpBucket, FollowUp[]> => {
    const groups: Record<FollowUpBucket, FollowUp[]> = {
        today: [],
        tomorrow: [],
        upcoming: [],
        overdue: [],
        completed: [],
        cancelled: []
    }

    followUps.forEach(followUp => groups[getFollowUpBucket(followUp, today)].push(followUp))

    FOLLOW_UP_BUCKETS.forEach(({ key }) => {
        groups[key] = sortFollowUps(key, groups[key])
    })

    return groups
}

/** The visit a follow-up came from, or the latest visit before it was created. */
export const getPreviousVisit = (followUp: FollowUp, index: ClinicIndex): Visit | null => {
    if (followUp.visitId) {
        const linked = index.visitsById.get(followUp.visitId)

        if (linked) return linked
    }

    const createdOn = dateFromTimestamp(followUp.createdAt)
    const visits = index.visitsByPatient.get(followUp.patientId) ?? []

    return visits.find(visit => visit.date <= createdOn) ?? visits[0] ?? null
}

export type ReminderTone = 'none' | 'whatsapp' | 'call' | 'warning' | 'confirmed' | 'muted'

export interface ReminderStatus {
    tone: ReminderTone
    label: string
    at: string | null
}

export const getReminderStatus = (followUp: FollowUp, index: ClinicIndex): ReminderStatus => {
    if (followUp.status === 'completed') return { tone: 'muted', label: 'Visit done', at: followUp.completedAt }
    if (followUp.status === 'cancelled') return { tone: 'muted', label: 'Cancelled', at: followUp.cancelledAt }
    if (followUp.confirmedAt) return { tone: 'confirmed', label: 'Confirmed', at: followUp.confirmedAt }

    const latest = index.remindersByFollowUp.get(followUp.id)?.[0]

    if (!latest) return { tone: 'none', label: 'Not reminded', at: null }

    if (latest.channel === 'call') {
        const outcome = latest.outcome ? CALL_OUTCOME_LABELS[latest.outcome] : 'Called'
        const tone: ReminderTone = latest.outcome === 'no-answer' || latest.outcome === 'reschedule' ? 'warning' : 'call'

        return { tone, label: latest.outcome === 'no-answer' ? 'Call: no answer' : `Call: ${outcome.toLowerCase()}`, at: latest.createdAt }
    }

    return { tone: 'whatsapp', label: 'WhatsApp reminder', at: latest.createdAt }
}

export interface TodayEntry {
    key: string
    patient: Patient
    kind: 'visited' | 'arrived' | 'expected'
    visit: Visit | null
    followUp: FollowUp | null
    time: string | null
}

export const getTodayEntries = (data: ClinicData, index: ClinicIndex, today: string): TodayEntry[] => {
    const visited = new Map<string, TodayEntry>()

    data.visits.forEach(visit => {
        if (visit.date !== today) return

        const patient = index.patientsById.get(visit.patientId)
        const existing = visited.get(visit.patientId)

        if (!patient || (existing?.visit && existing.visit.createdAt > visit.createdAt)) return

        visited.set(visit.patientId, {
            key: `visit-${visit.id}`,
            patient,
            kind: 'visited',
            visit,
            followUp: index.followUpsByVisit.get(visit.id) ?? null,
            time: timeFromTimestamp(visit.createdAt)
        })
    })

    const expected: TodayEntry[] = []
    const arrived = new Map<string, TodayEntry>()

    data.followUps.forEach(followUp => {
        if (visited.has(followUp.patientId)) return

        const patient = index.patientsById.get(followUp.patientId)

        if (!patient) return

        if (followUp.status === 'pending' && followUp.dueDate === today) {
            expected.push({
                key: `follow-up-${followUp.id}`,
                patient,
                kind: 'expected',
                visit: getPreviousVisit(followUp, index),
                followUp,
                time: followUp.dueTime
            })

            return
        }

        if (followUp.status === 'completed' && followUp.completedAt && dateFromTimestamp(followUp.completedAt) === today) {
            arrived.set(followUp.patientId, {
                key: `arrived-${followUp.id}`,
                patient,
                kind: 'arrived',
                visit: getPreviousVisit(followUp, index),
                followUp,
                time: timeFromTimestamp(followUp.completedAt)
            })
        }
    })

    expected.sort((a, b) => (a.time ?? '99:99').localeCompare(b.time ?? '99:99'))

    const seen = [...visited.values(), ...[...arrived.values()].filter(entry => !expected.some(item => item.patient.id === entry.patient.id))]
        .sort((a, b) => (b.time ?? '').localeCompare(a.time ?? ''))

    return [...expected, ...seen]
}

export interface DashboardCounts {
    totalPatients: number
    newThisMonth: number
    todayPatients: number
    dueToday: number
    upcoming: number
    overdue: number
}

export const getDashboardCounts = (data: ClinicData, today: string, todayEntries: TodayEntry[]): DashboardCounts => {
    let dueToday = 0
    let upcoming = 0
    let overdue = 0

    data.followUps.forEach(followUp => {
        if (followUp.status !== 'pending') return
        if (followUp.dueDate === today) dueToday += 1
        else if (followUp.dueDate > today) upcoming += 1
        else overdue += 1
    })

    return {
        totalPatients: data.patients.length,
        newThisMonth: data.patients.filter(patient => isSameMonth(dateFromTimestamp(patient.createdAt), today)).length,
        todayPatients: todayEntries.length,
        dueToday,
        upcoming,
        overdue
    }
}

const compact = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '')

/** Matches by name, phone digits, Patient ID or place. Best matches first. */
export const searchPatients = (patients: Patient[], query: string): Patient[] => {
    const text = query.trim().toLowerCase()

    if (!text) return patients

    const digits = digitsOnly(text)
    const compactQuery = compact(text)

    const scored = patients
        .map(patient => {
            const name = patient.name.toLowerCase()
            const id = compact(patient.id)
            const phone = digitsOnly(patient.phone)

            const score = Math.max(
                compactQuery && id === compactQuery ? 100 : 0,
                digits.length >= 2 && id.includes(compactQuery) ? 80 : 0,
                name.startsWith(text) ? 70 : 0,
                name.split(/\s+/).some(part => part.startsWith(text)) ? 60 : 0,
                name.includes(text) ? 50 : 0,
                digits.length >= 3 && phone.includes(digits) ? 45 : 0,
                compactQuery.length >= 3 && id.includes(compactQuery) ? 30 : 0,
                patient.place.toLowerCase().includes(text) ? 20 : 0
            )

            return { patient, score }
        })
        .filter(item => item.score > 0)

    return scored.sort((a, b) => b.score - a.score || a.patient.name.localeCompare(b.patient.name)).map(item => item.patient)
}

export const findPhoneMatches = (patients: Patient[], phone: string, excludeId?: string): Patient[] => {
    const digits = digitsOnly(phone).slice(-10)

    if (digits.length < 10) return []

    return patients.filter(patient => patient.id !== excludeId && digitsOnly(patient.phone).slice(-10) === digits)
}

/** The returning date to print: the linked visit's open follow-up, or the patient's open one when no visit is linked. */
export const getPrescriptionNextVisit = (
    prescription: { patientId: string, visitId: string | null, date: string },
    index: ClinicIndex
): { date: string, time: string | null } | null => {
    const linked = prescription.visitId ? index.followUpsByVisit.get(prescription.visitId) ?? null : null
    const followUp = prescription.visitId
        ? linked?.status === 'pending' ? linked : null
        : getStats(index, prescription.patientId).pendingFollowUp

    if (!followUp || followUp.status !== 'pending' || followUp.dueDate < prescription.date) return null

    return { date: followUp.dueDate, time: followUp.dueTime }
}

/** Last time each medicine name was prescribed, so a new row can be filled in automatically. */
export const getMedicineLastUses = (prescriptions: Prescription[]): Map<string, Prescription['medicines'][number]> => {
    const uses = new Map<string, Prescription['medicines'][number]>()
    const newestFirst = [...prescriptions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    newestFirst.forEach(prescription => {
        prescription.medicines.forEach(medicine => {
            const key = medicine.name.trim().toLowerCase()

            if (key && !uses.has(key)) uses.set(key, medicine)
        })
    })

    return uses
}
